import React, { useEffect, useState } from 'react';
import { Lock, Save, Loader2, Sparkles, Shield, Info } from 'lucide-react';
import { adminApi } from './api';

// 7 секций бланка с пояснениями «зачем» и метками полей
const SECTIONS = [
  {
    id: 'identity',
    title: 'Кто я и откуда',
    why: 'Чтобы Аня знала ваш бэкграунд и могла говорить от вашего лица с верными деталями.',
    fields: [
      { key: 'education', label: 'Образование', hint: 'Вуз, специальность, годы', multi: false },
      { key: 'trainings', label: 'Дополнительные обучения', hint: 'Курсы, мастер-классы, имена учителей', multi: true },
      { key: 'methods',   label: 'Методы которыми работаю', hint: 'КПТ / НЛП / телесные — глубина каждого', multi: true },
      { key: 'experience_years', label: 'Стаж практики', hint: 'С какого года, сколько клиентов прошло', multi: false },
    ],
  },
  {
    id: 'voice',
    title: 'Мой голос',
    why: 'Чтобы Аня писала вашими словами, а не «вообще как психолог».',
    fields: [
      { key: 'opening_phrase', label: 'Как я начинаю первую встречу', hint: 'Одной фразой' },
      { key: 'typical_phrases', label: 'Мои типичные формулировки', hint: '3-5 фраз которые я часто говорю клиентам', multi: true },
      { key: 'avoided_words', label: 'Каких слов избегаю', hint: 'И почему', multi: true },
      { key: 'writing_samples', label: 'Образцы моих текстов', hint: '2-3 ваших абзаца — например пост из соцсетей', multi: true },
    ],
  },
  {
    id: 'boundaries',
    title: 'Границы',
    why: 'Чтобы Аня не предлагала посетителю того, что вы не делаете, и понимала кризисный протокол.',
    fields: [
      { key: 'wont_work_with', label: 'С чем НЕ работаю', hint: 'Тяжёлые психиатрические диагнозы? Зависимости? Дети?', multi: true },
      { key: 'referrals_to', label: 'Когда направляю к другому специалисту', hint: 'И к каким именно', multi: true },
      { key: 'red_lines', label: 'Моя красная линия в работе с клиентом', multi: true },
      { key: 'crisis_protocol', label: 'Кризисный протокол', hint: 'Что делать если клиент упоминает самоповреждение или суицид', multi: true },
    ],
  },
  {
    id: 'thinking',
    title: 'Как я думаю',
    why: 'Чтобы Аня могла объяснить ваш подход тем, кто впервые открыл сайт.',
    fields: [
      { key: 'main_choice', label: 'Мой главный методологический выбор', hint: 'Одно предложение', multi: true },
      { key: 'vs_coaching', label: 'Чем мой подход отличается от коучинга', multi: true },
      { key: 'wont_do', label: 'Что я НЕ делаю — что от меня иногда ожидают', multi: true },
      { key: 'exit_signals', label: 'Как я понимаю что клиент готов выходить из терапии', multi: true },
    ],
  },
  {
    id: 'business',
    title: 'Деловая сторона',
    why: 'Чтобы Аня корректно отвечала на «сколько стоит», «когда удобно», «как записаться».',
    fields: [
      { key: 'pricing_logic', label: 'Как я ставлю цену', hint: 'По чему ориентируюсь', multi: true },
      { key: 'discount_logic', label: 'Когда могу сделать исключение по цене' },
      { key: 'schedule', label: 'Расписание', hint: 'Дни недели, часы', multi: true },
      { key: 'preferred_channel', label: 'Через какие каналы со мной удобнее', hint: 'Telegram / звонок / email' },
    ],
  },
  {
    id: 'inspiration',
    title: 'Вдохновение',
    why: 'Чтобы Аня могла порекомендовать клиенту то же, что и вы — без выдумок.',
    fields: [
      { key: 'authors', label: 'Авторы которых читаю и рекомендую', multi: true },
      { key: 'books_articles', label: 'Книги / статьи которые повлияли на мой метод', multi: true },
      { key: 'quotes', label: 'Цитаты которые ношу с собой', multi: true },
      { key: 'my_resource', label: 'Что мне самой помогает оставаться в ресурсе', multi: true },
    ],
  },
  {
    id: 'materials',
    title: 'Свободные материалы',
    why: 'Вставьте сюда что хотите: конспекты с обучений, эссе, важные заметки. Аня сама прочитает.',
    fields: [
      { key: 'free_text', label: 'Любые тексты', hint: 'Без структуры — просто пишите', multi: true, big: true },
    ],
  },
];

function SectionCard({ section, data, onSave, savingPassword }) {
  const [fields, setFields] = useState(() => {
    const initial = {};
    section.fields.forEach((f) => { initial[f.key] = data?.[f.key] || ''; });
    return initial;
  });
  const [pwd, setPwd] = useState(savingPassword || '');
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  // Синхронизация при внешнем обновлении (например, после первой загрузки)
  useEffect(() => {
    const next = {};
    section.fields.forEach((f) => { next[f.key] = data?.[f.key] || ''; });
    setFields(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const filledCount = Object.values(fields).filter((v) => v && v.trim()).length;
  const isDirty = section.fields.some((f) => (fields[f.key] || '') !== (data?.[f.key] || ''));

  // Эффективный пароль: локально введённый > сессионный
  const effectivePwd = pwd || savingPassword || '';

  const onSubmit = async () => {
    if (!effectivePwd) { setShowPwd(true); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(section.id, fields, effectivePwd);
      setSavedAt(new Date());
      setShowPwd(false);
    } catch (err) {
      setError(err.message || 'Не получилось сохранить');
      setShowPwd(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dark-card" style={{ padding: 26 }} data-testid={`knowledge-section-${section.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap" style={{ marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="dark-h2" style={{ fontSize: 24, marginBottom: 6 }}>{section.title}</h2>
          <p className="dark-meta" style={{ lineHeight: 1.6, fontStyle: 'italic' }}>{section.why}</p>
        </div>
        <span className="dark-tag" style={{ flexShrink: 0 }}>
          {filledCount}/{section.fields.length} заполнено
        </span>
      </div>

      <div style={{ marginTop: 16 }}>
        {section.fields.map((f) => (
          <div key={f.key} style={{ marginBottom: 14 }}>
            <label style={{
              display: 'block', fontSize: 12, color: 'var(--cream-soft)',
              textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
            }}>
              {f.label}
              {f.hint && <span style={{ textTransform: 'none', color: 'var(--cream-soft)', opacity: 0.7, marginLeft: 8, fontStyle: 'italic' }}>— {f.hint}</span>}
            </label>
            {f.multi ? (
              <textarea
                className="dark-input"
                rows={f.big ? 10 : 3}
                value={fields[f.key]}
                onChange={(e) => setFields((p) => ({ ...p, [f.key]: e.target.value }))}
                style={{ resize: 'vertical', minHeight: f.big ? 220 : 78, fontFamily: 'inherit' }}
                data-testid={`field-${section.id}-${f.key}`}
              />
            ) : (
              <input
                className="dark-input"
                value={fields[f.key]}
                onChange={(e) => setFields((p) => ({ ...p, [f.key]: e.target.value }))}
                data-testid={`field-${section.id}-${f.key}`}
              />
            )}
          </div>
        ))}
      </div>

      {(showPwd || (isDirty && !savingPassword)) && (
        <div style={{
          marginTop: 14, padding: 14,
          background: 'rgba(148, 0, 74, 0.06)',
          border: '1px solid rgba(148, 0, 74, 0.20)',
          borderRadius: 12,
        }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
            <Lock size={14} style={{ color: 'var(--wine-bright)' }} />
            <span style={{ fontSize: 12, color: 'var(--cream-mute)', fontStyle: 'italic' }}>
              Введите пароль ещё раз, чтобы перешифровать файл на диске
            </span>
          </div>
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="пароль"
            className="dark-input"
            data-testid={`section-pwd-${section.id}`}
          />
        </div>
      )}

      {error && (
        <div style={{ marginTop: 10, color: 'var(--wine-bright)', fontSize: 13 }} data-testid={`section-error-${section.id}`}>
          {error}
        </div>
      )}

      <div className="flex items-center justify-between" style={{ marginTop: 18, gap: 12, flexWrap: 'wrap' }}>
        <div className="dark-meta" style={{ fontSize: 12 }}>
          {savedAt ? (
            <span style={{ color: 'var(--gold)' }}>Сохранено {savedAt.toLocaleTimeString('ru-RU')}</span>
          ) : isDirty ? (
            'Есть несохранённые изменения'
          ) : (
            'Без изменений'
          )}
        </div>
        <button
          onClick={onSubmit}
          disabled={saving || !isDirty}
          className="dark-btn dark-btn-primary"
          data-testid={`section-save-${section.id}`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Шифрую…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}

export default function AdminKnowledge() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Пароль сохраняем в памяти компонента (не в localStorage) — чтобы не переспрашивать при сохранении соседних секций
  const [sessionPassword, setSessionPassword] = useState('');

  const load = async () => {
    try {
      const [d, s] = await Promise.all([adminApi.knowledgeGet(), adminApi.knowledgeStatus()]);
      setData(d.data);
      setStatus(s);
      setError('');
    } catch (err) {
      setError(err.message || 'Не получилось загрузить базу знаний');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (sectionId, fields, password) => {
    const resp = await adminApi.knowledgeUpdate(sectionId, fields, password);
    setSessionPassword(password);
    setData((prev) => ({ ...prev, [sectionId]: resp.section }));
    // Обновляем статус (количество заполненных секций)
    const s = await adminApi.knowledgeStatus();
    setStatus(s);
    return resp;
  };

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: '120px 0' }} data-testid="knowledge-loading">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--cream-soft)' }} />
      </div>
    );
  }

  const filled = status?.filled_sections || 0;
  const total = SECTIONS.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="admin-knowledge">
      {/* Шапка */}
      <div className="dark-card" style={{ padding: 28 }}>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="dark-eyebrow">Личное · только для Ани</div>
            <h1 className="dark-h1" style={{ fontSize: 42, marginTop: 6 }}>База знаний</h1>
            <p className="dark-meta" style={{ marginTop: 12, maxWidth: 640, lineHeight: 1.7 }}>
              Здесь вы рассказываете Ане о себе — методах, голосе, границах, любимых авторах.
              Каждая секция помогает ей звучать как вы, а не как «вообще психолог».
              Можно заполнять по одной секции, в любом порядке.
            </p>
          </div>

          <div className="flex flex-col gap-3" style={{ minWidth: 220 }}>
            <div className="flex items-center gap-3">
              <Shield size={18} style={{ color: 'var(--gold)' }} />
              <div>
                <div className="dark-eyebrow" style={{ fontSize: 10 }}>Защита</div>
                <div className="dark-meta" style={{ marginTop: 2 }}>
                  AES-256 + ваш пароль
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Sparkles size={18} style={{ color: 'var(--gold)' }} />
              <div>
                <div className="dark-eyebrow" style={{ fontSize: 10 }}>Формат</div>
                <div className="dark-meta" style={{ marginTop: 2 }}>
                  TOON — экономит токены Ани
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Прогресс заполнения */}
        <div style={{ marginTop: 22 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <span className="dark-meta">Заполнено секций</span>
            <span style={{
              fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic',
              fontSize: 22, color: 'var(--cream)',
            }}>
              {filled} <span className="dark-meta" style={{ fontSize: 14 }}>из {total}</span>
            </span>
          </div>
          <div style={{
            height: 6, borderRadius: 999,
            background: 'rgba(255,255,255,0.05)', overflow: 'hidden',
          }}>
            <div
              style={{
                height: '100%',
                width: `${(filled / total) * 100}%`,
                background: 'linear-gradient(90deg, var(--gold), var(--wine))',
                borderRadius: 999,
                transition: 'width 600ms ease',
                boxShadow: '0 0 12px rgba(197, 165, 114, 0.4)',
              }}
              data-testid="knowledge-progress"
            />
          </div>
        </div>
      </div>

      {/* Информационная плашка о приватности */}
      <div
        style={{
          padding: '14px 18px',
          background: 'rgba(197, 165, 114, 0.06)',
          border: '1px solid rgba(197, 165, 114, 0.18)',
          borderRadius: 14,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
        data-testid="privacy-note"
      >
        <Info size={16} style={{ color: 'var(--gold)', marginTop: 2, flexShrink: 0 }} />
        <p style={{ fontSize: 13, color: 'var(--cream-mute)', lineHeight: 1.6, margin: 0 }}>
          Файл хранится на диске в зашифрованном виде. Без вашего пароля никто — даже мы с другого устройства —
          его не прочитает. В оперативной памяти данные живут только пока активна ваша сессия (60 минут с продлением).
          При выходе всё стирается.
        </p>
      </div>

      {error && (
        <div className="dark-card" style={{ padding: 16, color: 'var(--wine-bright)' }} data-testid="knowledge-error">{error}</div>
      )}

      {/* Секции */}
      {SECTIONS.map((s) => (
        <SectionCard
          key={s.id}
          section={s}
          data={data?.[s.id]}
          onSave={handleSave}
          savingPassword={sessionPassword}
        />
      ))}
    </div>
  );
}
